import { EventBusy } from '@mui/icons-material';
import { IconButton, Tooltip, Typography } from '@mui/material';
import { red } from '@mui/material/colors';
import { action, computed, makeObservable, observable } from 'mobx';
import { inject, observer } from 'mobx-react';
import moment from 'moment';
import '../../css/processo.css';
import Configuracao from '../../util/Configuracao';
import ReactComponent from '../ReactComponent';
import ModalConfirmacao, { TIPO_CONFIRMACAO } from './ModalConfirmacao';

class RemoverProcessoPauta extends ReactComponent {
    modalConfirmacaoOpen = false;

    constructor(props) {
        super(props);

        makeObservable(this, {
            modalConfirmacaoOpen: observable,
            registro: computed,
            origemPJe: computed,
            excluirDaPauta: action,
            situacaoPauta: computed,
            situacaoPautaAberta: computed,
            processoJulgado: computed,
            callbackConfirmar: action,
            callbackCancelar: action,
            handleClickRemoverDaPauta: action,
            tooltipBotaoRemoverDaPauta: computed,
            corBotaoRemoverDaPauta: computed
        });
    }

    get registro() {
        return this.observableProps.registro;
    }

    get origemPJe() {
        return this.registro && this.registro.processo.origem === Configuracao.ORIGEM_PJE;
    }

    excluirDaPauta = async () => {
        await this.pautaJulgamentoStore.removerProcessoPauta(this.registro);
        this.mensagemStore.abrirMensagemSucesso('Processo removido da pauta com sucesso');
    };

    get situacaoPauta() {
        return this.pautaJulgamentoStore.pautaSelecionada && this.pautaJulgamentoStore.pautaSelecionada.situacao;
    }

    get situacaoPautaAberta() {
        return (
            this.situacaoPauta === Configuracao.STATUS_ABERTA ||
            this.situacaoPauta === Configuracao.STATUS_NAO_GERENCIADA_ABERTA
        );
    }

    callbackConfirmar = () => {
        this.modalConfirmacaoOpen = false;
        this.excluirDaPauta();
    };

    callbackCancelar = () => {
        this.modalConfirmacaoOpen = false;
    };

    handleClickRemoverDaPauta = () => {
        this.modalConfirmacaoOpen = true;
    };

    get processoJulgado() {
        return this.registro.resultadoJulgamento && this.registro.resultadoJulgamento.codigo !== 'N';
    }

    dataMaior = () => {
        return parseInt(
            moment(
                this.pautaJulgamentoStore.pautaSelecionada?.prazos?.dataInicioSessaoPresencial,
                'DD-MM-YYYY-HH-mm-ss'
            )
                .startOf('day')
                .format('YYYYMMDDhhmmss')
        ) >
            parseInt(
                moment(
                    this.pautaJulgamentoStore.pautaSelecionada?.prazos?.dataInicioVotacaoVirtual,
                    'DD-MM-YYYY-HH-mm-ss'
                )
                    .startOf('day')
                    .format('YYYYMMDDhhmmss')
            )
            ? moment(
                  this.pautaJulgamentoStore.pautaSelecionada?.prazos?.dataInicioVotacaoVirtual,
                  'DD-MM-YYYY-HH-mm-ss'
              )
                  .startOf('day')
                  .format('YYYY-MM-DD')
            : moment(
                  this.pautaJulgamentoStore.pautaSelecionada?.prazos?.dataInicioSessaoPresencial,
                  'DD-MM-YYYY-HH-mm-ss'
              )
                  .startOf('day')
                  .format('YYYY-MM-DD');
    };

    validaData = () => {
        const d1 = moment().format('YYYY-MM-DD');
        const d2 = this.dataMaior();
        const difMs = new Date(d2) - new Date(d1);
        const difDias = difMs / (1000 * 60 * 60 * 24);

        return difDias;
    };

    get tooltipBotaoRemoverDaPauta() {
        if (this.processoJulgado) {
            return 'Processo já julgado não pode ser removido da pauta.';
        }

        if (this.situacaoPautaAberta) {
            return 'Remover o processo da pauta';
        } else {
            return (
                <div>
                    Remover o processo da pauta
                    <br />
                    (Atenção: a pauta selecionada não está aberta!)
                </div>
            );
        }
    }

    get corBotaoRemoverDaPauta() {
        if (this.processoJulgado) {
            return 'inherited';
        }

        if (this.situacaoPautaAberta) {
            return 'black';
        } else {
            return 'darkorange';
        }
    }

    render() {
        const corAlerta = red[700];
        const totalDiasParaSessao = this.validaData();

        return (
            <>
                <Tooltip title={this.tooltipBotaoRemoverDaPauta}>
                    <span>
                        <IconButton
                            disabled={this.processoJulgado}
                            onClick={this.handleClickRemoverDaPauta}
                            style={{ padding: '8px' }}
                            size="large"
                        >
                            <EventBusy style={{ color: this.corBotaoRemoverDaPauta }} />
                        </IconButton>
                    </span>
                </Tooltip>
                <ModalConfirmacao
                    open={this.modalConfirmacaoOpen}
                    titulo={'Remoção de processo de pauta'}
                    tipoConfirmacao={TIPO_CONFIRMACAO.EXCLUSAO}
                    callbackConfirmar={this.callbackConfirmar}
                    callbackCancelar={this.callbackCancelar}
                >
                    <>
                        {this.registro.incluidoPeloGabinete === true && (
                            <Typography
                                color={corAlerta}
                                sx={{ mb: 1 }}
                            >
                                ATENÇÃO: Processo incluído na sessão.
                            </Typography>
                        )}
                        {this.registro.confirmadaPublicacaoPauta === true && (
                            <Typography
                                color={corAlerta}
                                sx={{ mb: 1 }}
                            >
                                ATENÇÃO: Processo publicado. Será necessário inserir edital no processo sinalizando a
                                remoção da pauta.
                            </Typography>
                        )}
                        {this.registro.confirmadaPublicacaoPauta !== true &&
                            this.registro.confirmadaInclusaoPauta === true && (
                                <Typography
                                    color={corAlerta}
                                    sx={{ mb: 1 }}
                                >
                                    ATENÇÃO: Processo confirmado em pauta.
                                </Typography>
                            )}
                        {totalDiasParaSessao <= 5 && totalDiasParaSessao > 0 && (
                            <Typography
                                color={corAlerta}
                                sx={{ mb: 1 }}
                            >
                                ATENÇÃO: Faltam {totalDiasParaSessao} dias para iniciar a sessão.
                            </Typography>
                        )}
                        <div>
                            {this.registro.pedidoSustentacaoOral && (
                                <Typography
                                    color={corAlerta}
                                    sx={{ mb: 1 }}
                                >
                                    ATENÇÃO: Há 1 ou mais registros de inscrições de advogados neste processo. Ao
                                    removê-lo da pauta, elas serão perdidas.
                                </Typography>
                            )}
                            <>
                                <Typography display={'inline'}>O processo </Typography>
                                <Typography
                                    display={'inline'}
                                    fontWeight={'bold'}
                                    noWrap
                                >
                                    {this.registro.processo.numeroCompleto}
                                </Typography>
                                <Typography display={'inline'}> será removido da pauta </Typography>
                                <Typography
                                    display={'inline'}
                                    fontWeight={'bold'}
                                    noWrap
                                >
                                    {this.pautaJulgamentoStore.pautaSelecionada.descricao}
                                </Typography>
                                <Typography display={'inline'}>. Deseja continuar?</Typography>
                            </>
                        </div>
                    </>
                </ModalConfirmacao>
            </>
        );
    }
}

export default inject('stores')(observer(RemoverProcessoPauta));
